/**
 * tabMetadata — SSOT for navigation tab identity, routing, and rail
 * presentation metadata. Introduced by slice 82 (audit gap
 * GAP-RAIL-TABTYPE-DUPLICATION) to consolidate the seven parallel
 * tables that previously sat across `useNavigationState.ts`
 * (ROUTE_TO_TAB / TAB_TO_ROUTE / TAB_TITLES / TabType union) and
 * `Sidebar.tsx` (LEGACY_TO_PRIMARY / PRIMARY_TO_LEGACY) + the
 * PrimaryRailKey union in `PrimaryRail.tsx`.
 *
 * Slice 86 (this file): completes the SSOT-migration by promoting
 * `conversations` to the canonical TabType discriminator (the legacy
 * 'messages' literal is retired). The PrimaryRailKey union is now a
 * pure 1:1 subset of TabType — the rail rename is no longer an
 * asymmetry. Consumers import `TabType` + `TAB_TITLES` + the typed
 * `TAB_KEYS` constants directly from this module (the re-exports in
 * `useNavigationState.ts` are gone). `useNavigationState.ts` carries a
 * one-shot localStorage migration that rewrites stored
 * `v1.nav.activeTab === 'messages'` to `'conversations'` on first
 * mount so returning users land on the same surface.
 *
 * SOLID / GRASP / DRY / KISS:
 *   - SRP: this file owns navigation metadata; nothing else.
 *   - DRY: ROUTE_TO_TAB / TAB_TO_ROUTE / TAB_TITLES /
 *          LEGACY_TO_PRIMARY / PRIMARY_TO_LEGACY /
 *          DEFAULT_PRIMARY_RAIL_ITEMS are all derived from TAB_METADATA.
 *   - GRASP (Information Expert): TAB_METADATA is the expert on every
 *     tab's identity, route, document title, and rail slot.
 *   - KISS: one Record literal; no class hierarchy; no factory.
 *
 * Backward compatibility (slice-82 → slice-86):
 *   - The TabType / PrimaryRailKey union literals are unchanged for the
 *     7 rail-visible tabs except the legacy 'messages' → 'conversations'
 *     rename (which folds the prior rail-side rename back into the tab
 *     union — eliminating the only TabType ↔ PrimaryRailKey asymmetry).
 *   - TAB_TITLES wire format preserved for the 10 unchanged keys.
 *   - LEGACY_TO_PRIMARY / PRIMARY_TO_LEGACY collapse to 1:1 identity
 *     for the 7 rail-visible tabs (conversations ↔ conversations).
 *   - DEFAULT_PRIMARY_RAIL_ITEMS preserves the canonical 7-item order
 *     (home → mentorship → conversations → schedule → submissions →
 *     insights → settings).
 *
 * Rollback safety: a single `git revert` of slice 86 restores the
 * 'messages' discriminator and the re-export shims. The `useNavigationState.ts`
 * localStorage migration is bidirectional-safe — on revert, stored
 * 'conversations' values fall through `readLS` to the 'home' default,
 * which the legacy code accepts.
 *
 * Slice horizon:
 *   - slice 82 (predecessor): introduce TAB_METADATA + derived exports;
 *     legacy 'messages' discriminator retained with rail-side rename.
 *   - slice 83 (predecessor): introduced slice-changelog.md.
 *   - slice 86 (this slice): retire the SSOT-migration shims —
 *     'messages' → 'conversations' in TAB_METADATA + every consumer;
 *     re-exports removed from useNavigationState.ts; LEGACY_TO_PRIMARY /
 *     PRIMARY_TO_LEGACY symbol imports inlined in Sidebar.tsx; inline
 *     eslint-disable comments on TabType discriminator strings folded
 *     into the typed TAB_KEYS constants object.
 *
 * Cross-references:
 *   - docs/redesign/mentorship-platform-redesign-2026-06-03.md §22.11 (slice 82), §22.15 (this slice)
 *   - docs/redesign/plan-completeness-audit-2026-06-03.md
 *     (GAP-RAIL-TABTYPE-DUPLICATION)
 *   - slice 70 (commit b7df87a) — 7-key user-intent IA ratification.
 */

import { GLOSSARY } from '../../modules/mentorship/glossary';
import { type ActiveFeatureKey } from './featureRegistry';

// ── Canonical key union (TabType) ──────────────────────────────────────
//
// Declared as a `const` tuple so we can both (a) export TabType as the
// literal-union type and (b) iterate the keys at module load to build
// derived records without TypeScript complaining about index-signature
// mismatches.
//
// Order matters: it is the canonical declaration order used by the
// DEFAULT_PRIMARY_RAIL_ITEMS derivation (after filtering to
// rail-visible rows). Reordering this list reshuffles the rail UI, so
// any change here MUST be paired with a rail-UX review.
// Phase 4.1 (Feature Surface Registry Gate): 'submissions' is dropped
// from the navigation model (FEATURE_REGISTRY status 'hidden' — workflow-
// first 6-key rail pivot). 'schedule' and 'insights' remain in the tuple
// (FEATURE_REGISTRY status 'stub') so their deep-link URLs stay
// addressable; they are demoted to non-rail (fallbackRailKey) below.
const TAB_KEY_TUPLE = [
    'home',
    'mentorship',
    'schedule',
    'conversations',
    'insights',
    'settings',
    'assistant',
    'education',
    'networking',
    'profile',
] as const;

export type TabType = typeof TAB_KEY_TUPLE[number];

/**
 * TAB_KEYS — typed constants for every TabType discriminator value.
 *
 * Why: prior to slice 86, ~4 call sites carried inline
 * `eslint-disable-next-line chiefy/no-naked-concept-strings` comments
 * to justify a bare `'submissions'` / `'insights'` / etc. literal as a
 * routing infrastructure id. Folding the strings into a typed namespace
 * here lets every consumer write `TAB_KEYS.SUBMISSIONS` and drops the
 * inline disable. The values are `as const` and type-equivalent to the
 * TabType union, so existing comparisons stay byte-identical.
 *
 * SRP: TAB_KEYS is a presentation-of-keys helper, not a registry. The
 * SSOT for which keys exist remains TAB_KEY_TUPLE / TabType.
 */
export const TAB_KEYS = {
    HOME: 'home',
    MENTORSHIP: 'mentorship',
    SCHEDULE: 'schedule',
    CONVERSATIONS: 'conversations',
    INSIGHTS: 'insights',
    SETTINGS: 'settings',
    ASSISTANT: 'assistant',
    EDUCATION: 'education',
    NETWORKING: 'networking',
    PROFILE: 'profile',
} as const satisfies Record<string, TabType>;

// ── Per-tab metadata row ───────────────────────────────────────────────
//
// `exactOptionalPropertyTypes` is on (tsconfig.app.json §27), so optional
// fields are typed as plain `?:` — passing `undefined` explicitly is
// rejected. Consumers omit fields they don't need.
type TabMetadataRow = {
    /** Document-title label. Always present. May route through GLOSSARY. */
    title: string;
    /** Optional SPA pathname. Omitted for tabs that have no URL route
     *  (assistant submenu, profile, etc.). */
    route?: string;
    /**
     * Rail slot key. Present iff this tab maps to a primary-rail slot.
     * Slice 86 collapsed the legacy `messages → conversations` rename so
     * every rail-visible tab now self-maps: `railKey === tab key`. */
    railKey?: PrimaryRailKey;
    /** Optional override for the rail label. When omitted the rail
     *  label is read from `title` (preserves the slice-70 contract that
     *  rail labels match tab titles). */
    railLabel?: string;
    /**
     * Fallback rail key for non-rail tabs (assistant / education /
     * networking / profile). The legacy Sidebar shim uses this to
     * highlight a sensible rail item when one of these tabs is active.
     * Required when `railKey` is omitted; ignored when `railKey` is set.
     */
    fallbackRailKey?: PrimaryRailKey;
};

// ── PrimaryRailKey / PRIMARY_RAIL_KEYS ─────────────────────────────────
//
// PRIMARY_RAIL_KEYS is the rail-of-record: the ordered list of keys
// that render as primary navigation slots. The `readonly ActiveFeatureKey[]`
// annotation makes it a COMPILE-TIME ERROR to list a key whose
// FEATURE_REGISTRY status is 'stub' or 'hidden' — closing the
// navigation-graph / feature-graph drift gap that slice 82 left open.
//
// Removed from the rail (vs. pre-registry list):
//   - 'submissions' — FEATURE_REGISTRY status 'hidden' (workflow-first
//     6-key pivot dropped Submissions).
//   - 'schedule'    — FEATURE_REGISTRY status 'stub' (ScheduleStubPage
//     is a placeholder; promote back to the rail when the full
//     CalendarAgendaSplit surface lands and status flips to 'active').
//   - 'insights'    — FEATURE_REGISTRY status 'stub' (InsightsStubPage
//     is a placeholder; promote back to the rail when the curated
//     insights surface lands and status flips to 'active').
//
// Deep-link routes for 'schedule' and 'insights' remain registered in
// TAB_METADATA (their stub pages stay addressable by URL); only the
// rail entry is gated by the type. 'submissions' is removed from
// TAB_METADATA entirely.
//
// Order = canonical declaration order. Rail UI reads from this tuple,
// so any reorder is a UX-breaking change.
// `satisfies` keeps the narrow tuple type (so PrimaryRailKey collapses
// to the 4-key literal union via `typeof PRIMARY_RAIL_KEYS[number]`)
// while still enforcing the ActiveFeatureKey constraint — a 'stub' or
// 'hidden' key in this tuple is a TS2322 at the literal site.
// Exported (vs the prior file-private const) so the Phase 4.1
// featureRegistry CI gate can iterate the rail tuple at test time.
export const PRIMARY_RAIL_KEYS = [
    'home',
    'mentorship',
    'conversations',
    'settings',
] as const satisfies readonly ActiveFeatureKey[];

export type PrimaryRailKey = typeof PRIMARY_RAIL_KEYS[number];

const PRIMARY_RAIL_KEY_SET = new Set<string>(PRIMARY_RAIL_KEYS);

/** Type guard: narrow an arbitrary string to a PrimaryRailKey. */
export function isPrimaryRailKey(value: string): value is PrimaryRailKey {
    return PRIMARY_RAIL_KEY_SET.has(value);
}

// ── TAB_METADATA — the single source of truth ──────────────────────────
//
// One row per TabType key. The shape of every row is symmetric: any
// downstream record (ROUTE_TO_TAB / TAB_TO_ROUTE / TAB_TITLES /
// LEGACY_TO_PRIMARY / PRIMARY_TO_LEGACY / DEFAULT_PRIMARY_RAIL_ITEMS)
// is a pure derivation of this object.
//
// `Readonly` discourages accidental mutation from consumers; the union
// `Record<TabType, …>` makes the object exhaustive at compile time —
// adding a new TabType key without a row here is a type error.
export const TAB_METADATA: Readonly<Record<TabType, TabMetadataRow>> = {
    home: {
        title: 'Home',
        route: '/',
        railKey: TAB_KEYS.HOME,
    },
    mentorship: {
        title: 'Mentorship',
        // Why: parity with the route registered in ROUTE_TO_TAB pre-slice-82
        // — clicking Mentorship from a non-home tab now updates the URL too,
        // closing the URL ↔ tab drift documented in useNavigationState.ts.
        route: '/mentorship',
        railKey: TAB_KEYS.MENTORSHIP,
    },
    schedule: {
        // Phase 4.1: FEATURE_REGISTRY status 'stub'. Route stays so the
        // deep-link URL remains addressable for tests / external bookmarks,
        // but the rail entry is gone (PRIMARY_RAIL_KEYS rejects stub keys
        // at the type level). Fallback rail key 'home' is the closest
        // semantic neighbour while the full surface lands in a follow-up
        // slice — when status flips to 'active' restore `railKey` here.
        title: 'Schedule',
        route: '/schedule',
        fallbackRailKey: TAB_KEYS.HOME,
    },
    conversations: {
        // Why (slice 86): `conversations` is now the canonical TabType
        // discriminator (the legacy 'messages' literal was retired). The
        // rail UX always rendered this row as "Conversations", so the
        // tab title now matches the rail label byte-for-byte — no
        // asymmetry to encode. The `Messages` title is preserved as the
        // user-facing copy for the inbox surface (no copy change in
        // slice 86); consumers wanting the old label use
        // `TAB_METADATA.conversations.title`.
        title: 'Messages',
        railKey: TAB_KEYS.CONVERSATIONS,
        railLabel: 'Conversations',
    },
    insights: {
        // Phase 4.1: FEATURE_REGISTRY status 'stub'. Route stays so the
        // deep-link URL remains addressable; rail entry retired. Fallback
        // rail key 'home' matches the slice-86 fallbackRailKey convention
        // for non-rail tabs (assistant/education/networking).
        title: GLOSSARY.INSIGHT.pluralLabel,
        // eslint-disable-next-line chiefy/no-naked-concept-strings -- URL pathname value (infrastructure id), not user copy.
        route: '/insights',
        fallbackRailKey: TAB_KEYS.HOME,
    },
    settings: {
        title: 'Settings',
        route: '/settings',
        railKey: TAB_KEYS.SETTINGS,
    },
    assistant: {
        title: 'Assistant',
        route: '/assists',
        // Why: assistant is a submenu under the legacy NavGroups branch with
        // no dedicated rail slot. When the rail-cohort is active and the
        // user is on assistant, highlight 'home' (closest semantic neighbour
        // — preserves the pre-slice-82 LEGACY_TO_PRIMARY mapping verbatim).
        fallbackRailKey: TAB_KEYS.HOME,
    },
    education: {
        title: 'Education',
        route: '/education',
        // Why: education sits inside the Learn section under NavGroups; no
        // rail slot. Fallback to 'home' matches pre-slice-82 behaviour.
        fallbackRailKey: TAB_KEYS.HOME,
    },
    networking: {
        title: 'Networking',
        route: '/networking',
        // Why: networking sits inside the Connect section under NavGroups;
        // no rail slot. Fallback to 'home' matches pre-slice-82 behaviour.
        fallbackRailKey: TAB_KEYS.HOME,
    },
    profile: {
        title: 'Profile',
        route: '/profile',
        // Why route exists: useNavigationState derives activeTab from
        // `ROUTE_TO_TAB[location.pathname] ?? storedTab`, so a tab with
        // no route is silently overridden by whatever URL the user is
        // on. The original "no SPA route" design intended profile to be
        // a modal-like overlay opened from UserMenu / SidebarFooter, but
        // setStoredTab('profile') from any /home, /mentorship, /settings
        // URL had no effect — activeTab kept resolving back to the URL
        // tab. Promoting profile to a real route fixes the
        // HomeProfileNudge "Finish your profile" CTA, the avatar click
        // in SidebarFooter, and every onNavigate('profile') call site.
        // Fallback rail key stays 'settings' for legacy consumers.
        fallbackRailKey: TAB_KEYS.SETTINGS,
    },
};

// ── Derived: ROUTE_TO_TAB ──────────────────────────────────────────────
//
// Built from the `route` field on each TAB_METADATA row. Pathname is
// the key, TabType is the value. Tabs without a route are skipped.
function buildRouteToTab(): Record<string, TabType> {
    const out: Record<string, TabType> = {};
    for (const key of TAB_KEY_TUPLE) {
        const row = TAB_METADATA[key];
        if (row.route) out[row.route] = key;
    }
    return out;
}

export const ROUTE_TO_TAB: Readonly<Record<string, TabType>> = buildRouteToTab();

// ── Derived: TAB_TO_ROUTE ──────────────────────────────────────────────
//
// Inverse of ROUTE_TO_TAB. `Partial` because not every TabType row has
// a route field (assistant submenu + profile in particular).
function buildTabToRoute(): Partial<Record<TabType, string>> {
    const out: Partial<Record<TabType, string>> = {};
    for (const key of TAB_KEY_TUPLE) {
        const row = TAB_METADATA[key];
        if (row.route) out[key] = row.route;
    }
    return out;
}

export const TAB_TO_ROUTE: Readonly<Partial<Record<TabType, string>>> = buildTabToRoute();

// ── Derived: TAB_TITLES ────────────────────────────────────────────────
//
// Exhaustive `Record<TabType, string>`. Wire format preserved for the
// 10 unchanged keys; the `messages` key is renamed to `conversations`
// (slice 86) with the same `'Messages'` value (no copy change).
function buildTabTitles(): Record<TabType, string> {
    // Why: cast through `Record<string, string>` and then re-narrow. The
    // loop assigns every TabType key (TAB_KEY_TUPLE is the source-of-truth
    // for exhaustiveness), so the final object IS a Record<TabType,
    // string>. Avoids an `as Record<TabType, string>` cast on a half-
    // built object, which would lie to the type system during the loop.
    const accum: Partial<Record<TabType, string>> = {};
    for (const key of TAB_KEY_TUPLE) {
        accum[key] = TAB_METADATA[key].title;
    }
    return accum as Record<TabType, string>;
}

export const TAB_TITLES: Readonly<Record<TabType, string>> = buildTabTitles();

// ── Derived: LEGACY_TO_PRIMARY ─────────────────────────────────────────
//
// Each TabType maps to its rail slot. For rail-visible tabs the slot is
// the `railKey` field; for non-rail tabs (assistant / education /
// networking / profile) the slot is the explicit `fallbackRailKey`.
// `fallbackRailKey` is mandatory when `railKey` is absent — the helper
// asserts on this so a future rowmate without either field surfaces
// as a load-time error instead of silently mapping to undefined.
//
// Slice 86: now collapses to a 1:1 identity for every rail-visible
// tab; only the 4 non-rail tabs use a non-identity mapping.
function buildLegacyToPrimary(): Record<TabType, PrimaryRailKey> {
    const accum: Partial<Record<TabType, PrimaryRailKey>> = {};
    for (const key of TAB_KEY_TUPLE) {
        const row = TAB_METADATA[key];
        const slot = row.railKey ?? row.fallbackRailKey;
        if (!slot) {
            throw new Error(
                `tabMetadata: TAB_METADATA["${key}"] has neither railKey nor `
                + 'fallbackRailKey. Every TabType must resolve to a rail slot.',
            );
        }
        accum[key] = slot;
    }
    return accum as Record<TabType, PrimaryRailKey>;
}

export const LEGACY_TO_PRIMARY: Readonly<Record<TabType, PrimaryRailKey>> = buildLegacyToPrimary();

// ── Derived: PRIMARY_TO_LEGACY ─────────────────────────────────────────
//
// Inverse mapping: each PrimaryRailKey maps to the canonical TabType it
// represents. Slice 86 collapses to a 1:1 identity for every entry.
function buildPrimaryToLegacy(): Record<PrimaryRailKey, TabType> {
    const accum: Partial<Record<PrimaryRailKey, TabType>> = {};
    for (const key of TAB_KEY_TUPLE) {
        const row = TAB_METADATA[key];
        if (row.railKey && !accum[row.railKey]) {
            // Only the FIRST row to claim a rail slot wins (TAB_KEY_TUPLE
            // declaration order). If two rows claimed the same slot it
            // would be a metadata bug, surfaced here at module load.
            accum[row.railKey] = key;
        }
    }
    // Exhaustiveness check: every PrimaryRailKey must have a TabType.
    for (const railKey of PRIMARY_RAIL_KEYS) {
        if (!accum[railKey]) {
            throw new Error(
                `tabMetadata: PRIMARY_RAIL_KEYS includes "${railKey}" but no `
                + 'TAB_METADATA row claims it via `railKey`. The two are out '
                + 'of sync — fix the metadata.',
            );
        }
    }
    return accum as Record<PrimaryRailKey, TabType>;
}

export const PRIMARY_TO_LEGACY: Readonly<Record<PrimaryRailKey, TabType>> = buildPrimaryToLegacy();

// ── Derived: DEFAULT_PRIMARY_RAIL_ITEMS ────────────────────────────────
//
// The canonical 7-item rail in slice-70 declaration order. Built from
// PRIMARY_RAIL_KEYS so the order is the rail-of-record order, and the
// label is `railLabel ?? title` from the row that claims each slot via
// PRIMARY_TO_LEGACY.
//
// Shape matches the legacy export verbatim:
//   ReadonlyArray<{ key: PrimaryRailKey; label: string }>
// — added a `Required<Pick<…, 'key' | 'label'>>` shape to match the
// PrimaryRail.tsx export contract so the file-level rename in slice 83
// is a one-line move.
export type DefaultPrimaryRailItem = {
    key: PrimaryRailKey;
    label: string;
};

function buildDefaultPrimaryRailItems(): ReadonlyArray<DefaultPrimaryRailItem> {
    const items: DefaultPrimaryRailItem[] = [];
    for (const railKey of PRIMARY_RAIL_KEYS) {
        const tabKey = PRIMARY_TO_LEGACY[railKey];
        const row = TAB_METADATA[tabKey];
        items.push({
            key: railKey,
            label: row.railLabel ?? row.title,
        });
    }
    return items;
}

export const DEFAULT_PRIMARY_RAIL_ITEMS: ReadonlyArray<DefaultPrimaryRailItem>
    = buildDefaultPrimaryRailItems();

// ── Sanity check: TAB_KEY_TUPLE exhaustiveness ─────────────────────────
//
// If a future TabType key is added without a TAB_METADATA row (or vice
// versa) TypeScript catches it at compile time via the
// `Record<TabType, TabMetadataRow>` constraint above. This module-load
// pass is a runtime belt-and-braces check for the (unlikely) case where
// the type system is bypassed via `as any`.
(() => {
    for (const key of TAB_KEY_TUPLE) {
        if (!TAB_METADATA[key]) {
            throw new Error(
                `tabMetadata: TAB_KEY_TUPLE lists "${key}" but TAB_METADATA has no `
                + 'row for it. The two are out of sync — fix the metadata.',
            );
        }
    }
})();
